import { useQuery } from "@tanstack/vue-query";
import { computed, unref, type MaybeRef } from "vue";

import * as celonis from "@/src/api/celonis";
import { queryClient } from "@/src/queries/client";
import { keys } from "@/src/queries/keys";
import { OP, trackPending } from "@/src/queries/pending";

type Id = MaybeRef<string | null | undefined>;

const enabledWhen = (...ids: Id[]) => computed(() => ids.every((id) => Boolean(unref(id))));

/**
 * Celonis metadata is the same for every component that asks for it and changes rarely, so each
 * of these is defined once and shared through the query cache.
 *
 * Every entry comes in two forms: a `use*` composable for components that want reactive data and
 * loading state, and a `fetch*` function for imperative callers such as store actions. Both go
 * through the same key, so a component mounting while an action is in flight joins that request
 * instead of starting a second one.
 */

export function useDatapools() {
    return useQuery({
        queryKey: keys.celonis.datapools(),
        queryFn: () => celonis.listDatapools(),
    });
}

export function fetchDatapools() {
    return trackPending(OP.GET_DATAPOOLS, () =>
        queryClient.fetchQuery({
            queryKey: keys.celonis.datapools(),
            queryFn: () => celonis.listDatapools(),
        }),
    );
}

export function useDatamodels(datapoolId: Id) {
    return useQuery({
        queryKey: computed(() => keys.celonis.datamodels(unref(datapoolId))),
        queryFn: () => celonis.listDatamodels(unref(datapoolId)!),
        enabled: enabledWhen(datapoolId),
    });
}

export function fetchDatamodels(datapoolId: string) {
    return trackPending(OP.GET_DATAMODELS, () =>
        queryClient.fetchQuery({
            queryKey: keys.celonis.datamodels(datapoolId),
            queryFn: () => celonis.listDatamodels(datapoolId),
        }),
    );
}

export function useActivities(datapoolId: Id, datamodelId: Id) {
    return useQuery({
        queryKey: computed(() => keys.celonis.activities(unref(datapoolId), unref(datamodelId))),
        queryFn: () => celonis.listActivities(unref(datapoolId)!, unref(datamodelId)!),
        enabled: enabledWhen(datapoolId, datamodelId),
    });
}

export function fetchActivities(datapoolId: string, datamodelId: string) {
    return trackPending(OP.GET_ACTIVITIES, () =>
        queryClient.fetchQuery({
            queryKey: keys.celonis.activities(datapoolId, datamodelId),
            queryFn: () => celonis.listActivities(datapoolId, datamodelId),
        }),
    );
}

export function useTables(datapoolId: Id, datamodelId: Id) {
    return useQuery({
        queryKey: computed(() => keys.celonis.tables(unref(datapoolId), unref(datamodelId))),
        queryFn: () => celonis.listTables(unref(datapoolId)!, unref(datamodelId)!),
        enabled: enabledWhen(datapoolId, datamodelId),
    });
}

export function fetchTables(datapoolId: string, datamodelId: string) {
    return trackPending(OP.GET_TABLES, () =>
        queryClient.fetchQuery({
            queryKey: keys.celonis.tables(datapoolId, datamodelId),
            queryFn: () => celonis.listTables(datapoolId, datamodelId),
        }),
    );
}

export function useCaseTables(datapoolId: Id, datamodelId: Id) {
    return useQuery({
        queryKey: computed(() => keys.celonis.caseTables(unref(datapoolId), unref(datamodelId))),
        queryFn: () => celonis.listCaseTables(unref(datapoolId)!, unref(datamodelId)!),
        enabled: enabledWhen(datapoolId, datamodelId),
    });
}

export function fetchCaseTables(datapoolId: string, datamodelId: string) {
    return trackPending(OP.GET_CASE_TABLES, () =>
        queryClient.fetchQuery({
            queryKey: keys.celonis.caseTables(datapoolId, datamodelId),
            queryFn: () => celonis.listCaseTables(datapoolId, datamodelId),
        }),
    );
}

export function useActivityTableOptions(datapoolId: Id, datamodelId: Id) {
    return useQuery({
        queryKey: computed(() => keys.celonis.activityTables(unref(datapoolId), unref(datamodelId))),
        queryFn: () => celonis.listActivityTableOptions(unref(datapoolId)!, unref(datamodelId)!),
        enabled: enabledWhen(datapoolId, datamodelId),
    });
}

export function fetchActivityTableOptions(datapoolId: string, datamodelId: string) {
    return trackPending(OP.GET_ACTIVITY_TABLES, () =>
        queryClient.fetchQuery({
            queryKey: keys.celonis.activityTables(datapoolId, datamodelId),
            queryFn: () => celonis.listActivityTableOptions(datapoolId, datamodelId),
        }),
    );
}

/**
 * Columns used to be memoised onto the table object in the store; the cache does that now, keyed
 * by the table so switching tables back and forth costs nothing.
 */
export function fetchTableColumns(datapoolId: string, datamodelId: string, tableId: string) {
    return trackPending(OP.GET_TABLE_COLUMNS, () =>
        queryClient.fetchQuery({
            queryKey: keys.celonis.columns(datapoolId, datamodelId, tableId),
            queryFn: () => celonis.listTableColumns(datapoolId, datamodelId, tableId),
        }),
    );
}

export function useTemplates() {
    return useQuery({
        queryKey: keys.celonis.templates(),
        queryFn: () => celonis.listTemplates(),
    });
}

export function fetchTemplates() {
    return trackPending(OP.GET_TEMPLATES, () =>
        queryClient.fetchQuery({
            queryKey: keys.celonis.templates(),
            queryFn: () => celonis.listTemplates(),
        }),
    );
}
